import { setNotificationEnabled } from "@diacare/common/actions/actions";
import { Collections } from "@diacare/common/models/collections";
import { Notification } from "@diacare/common/models/notification";
import {
  Button,
  Cell,
  DeleteButton,
  EditButton,
  GridRow,
  HeadCell,
  SimpleModal,
  Switch,
  spacing,
  useModal,
} from "@litbase/alexandria";
import { ListCard } from "@litbase/alexandria/components/layout/list/list-card";
import { CustomListPage } from "@litbase/alexandria/components/layout/list/list-page";
import { client } from "@litbase/client";
import { litql } from "@litbase/core";
import { useQuery } from "@litbase/react";
import { Plus } from "@styled-icons/boxicons-regular";
import { useState } from "react";
import styled from "styled-components";

export function NotificationListPage() {
  const [notifications] = useQuery<Notification>(Collections.NOTIFICATIONS, {
    $live: true,
    $matchAll: {},
    $sort: { createdAt: -1 },
  });

  return (
    <CustomListPage title="Notifications" addButtonLink="/notifications/edit/new" hideAddButtons>
      <AddButton to="/notifications/edit/new">
        <Plus />
        Add a new notification
      </AddButton>
      <NotificationList items={Array.isArray(notifications) ? notifications : []} />
    </CustomListPage>
  );
}

function NotificationList({ items }: { items: Notification[] }) {
  const { showModal, dismissModal } = useModal();

  return (
    // @ts-expect-error
    <ListCard
      columns="2fr 2fr 1fr 1fr"
      items={items}
      headerRowComponent={
        <GridRow>
          <HeadCell>Created at</HeadCell>
          <HeadCell>Title</HeadCell>
          <HeadCell>Enabled</HeadCell>
          <HeadCell></HeadCell>
        </GridRow>
      }
      rowComponent={(item: Notification) => (
        <NotificationListRow
          item={item}
          onDelete={() =>
            showModal(
              <SimpleModal>
                <span>Are you sure you want to delete this notification?</span>
                <Row>
                  <Button onClick={() => dismissModal()}>Cancel</Button>
                  <Button
                    $kind="danger"
                    onClick={() => {
                      litql.query(Collections.NOTIFICATIONS, { $match: { _id: item._id }, $pull: 1 });
                      dismissModal();
                    }}
                  >
                    Delete
                  </Button>
                </Row>
              </SimpleModal>,
            )
          }
        />
      )}
    />
  );
}

function NotificationListRow({ item, onDelete }: { item: Notification; onDelete: () => void }) {
  const [isSaving, setIsSaving] = useState(false);

  return (
    <GridRow>
      <Cell>{item.createdAt?.toLocaleString()}</Cell>
      <Cell>{item.title}</Cell>
      <Cell>
        <Switch
          value={!!item.enabled}
          disabled={isSaving}
          onChange={async (enabled) => {
            setIsSaving(true);

            try {
              await client.call(setNotificationEnabled, item._id, enabled);
            } finally {
              setIsSaving(false);
            }
          }}
        />
      </Cell>
      <Cell>
        <EditButton to={`/notifications/edit/` + item._id.toUrlSafeString()} />
        <DeleteButton onClick={onDelete} />
      </Cell>
    </GridRow>
  );
}

const Row = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${spacing._6};

  > * + * {
    margin-left: ${spacing._4};
  }
`;

const AddButton = styled(Button)`
  width: fit-content;
`;
